import { useState } from 'react';
import { LogoMark } from '../../components/LogoMark';
import { Link } from 'react-router-dom';
import { Mail, Loader2, ArrowLeft } from 'lucide-react';
import { forgotPassword } from '../../api/auth';
import { humanizeAuthError } from '../../utils/authErrors';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      await forgotPassword(email);
      setIsSubmitted(true);
    } catch (err: unknown) {
      setError(
        humanizeAuthError(err, 'Something went wrong. Please try again.', {
          networkMessage: 'Server isn’t ready yet. Wait a moment and try again.',
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center px-4 pt-safe">
        <div className="w-full max-w-md">
          <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-xl shadow-card border border-border-subtle-light dark:border-border-subtle-dark p-8">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-950/20 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                Check Your Email
              </h1>
              <p className="text-text-secondary-light dark:text-text-secondary-dark">
                If an account exists for <strong>{email}</strong>, you will receive a password reset
                link shortly.
              </p>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-primary-50 dark:bg-primary-950/20 border border-primary-200 dark:border-primary-900/30 rounded-lg">
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  The reset link will expire in 1 hour. If you don't receive an email, check your
                  spam folder or try again.
                </p>
              </div>

              <Link
                to="/login"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 border border-border-hover-light dark:border-border-hover-dark rounded-lg bg-surface-raised-light dark:bg-surface-base-dark text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-base-light dark:hover:bg-surface-hover-dark transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center px-4 pt-safe">
      <div className="w-full max-w-md">
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-xl shadow-card border border-border-subtle-light dark:border-border-subtle-dark p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <LogoMark className="h-10 w-auto" />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
              Reset your password
            </h1>
            <p className="text-text-secondary-light dark:text-text-secondary-dark">
              Enter your email and we'll send you a reset link
            </p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1.5"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-border-hover-light dark:border-border-hover-dark rounded-lg bg-surface-raised-light dark:bg-surface-base-dark text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-shadow"
                placeholder="you@example.com"
                autoComplete="email"
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-700 hover:bg-primary-800 dark:glow-primary dark:hover:glow-primary-hover text-white font-medium rounded-lg shadow-subtle hover:shadow-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Send Reset Link
                </>
              )}
            </button>
          </form>

          <div className="mt-6">
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-700 dark:hover:text-primary-400 transition-colors"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
